import React, { useState, useEffect, useCallback } from "react";
import { ScrollArea } from "../../components/ui/scroll-area";
import PDF_ICON from "../../assets/PDF_ICON_2.png";
import { PAGE_LIMIT } from "../../constants/text_constants";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import userLogout from "../../utils/userLogout";
import { useDispatch } from "react-redux";
import { modExp } from "../../utils/util-functions/cryptoUtils";
import DecodePdfUrl from "../../utils/util-functions/DecryptionUtil";
import PDFDialogButton from "../PDF-View-Component/PDFDialogButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import handleBuy from "../Buy/handleBuy";
import { ITEM_COURSE } from "../../constants/text_constants";

const ClassroomTabNotes = ({
  subjectId,
  defaultSubjectId,
  isPurchased,
  searchText,
  data,
}) => {
  const [notesData, setNotesData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userAuthenticated = sessionStorage.getItem("authenticated") === "1";

  const [encryptionData, setEncryptionData] = useState(null);

  const setPubPvtKey = () => {
    const prime = 23;
    const base = 5;

    const clientPrivateKey = Math.floor(Math.random() * 100);
    const clientPublicKey = modExp(base, clientPrivateKey, prime);

    const cryptoData = {
      clientPrivateKey: clientPrivateKey,
      clientPublicKey: clientPublicKey,
      prime: prime,
    };

    setEncryptionData(cryptoData);
  };

  const fetchSubjectNotes = useCallback(async (retries = 6) => {
    try {
      const authToken = sessionStorage.getItem("token");
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      };
      const response = await fetch(
        process.env
          .REACT_APP_RANK_PLUS_COURSE_DETAILS_LIVE_CLASS_SUBJECT_NOTES +
        (subjectId ? subjectId : defaultSubjectId) +
        "&limit=250&page=" +
        currentPage +
        `&publicKey=${encryptionData.clientPublicKey}`,
        requestOptions
      );
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          userLogout(navigate, dispatch, true);
        }
        if (response.status >= 500 && response.status < 600 && retries > 0) {
          console.warn(`Retrying... attempts left: ${retries}`);
          setTimeout(() => fetchSubjectNotes(retries - 1), 10000);
          return;
        } else {
          throw new Error(`HTTP error: ${response.status}`);
        }
      }
      const json = await response.json();
      setNotesData(json?.data);
      setTotalPages(Math.ceil(json?.data?.total / PAGE_LIMIT));
    } catch (err) {
      console.error("Error fetchSubjectNotes", err);
    }
  }, [
    currentPage,
    defaultSubjectId,
    dispatch,
    encryptionData,
    navigate,
    subjectId,
  ]);

  useEffect(() => {
    if (
      encryptionData?.clientPublicKey != null &&
      (subjectId || defaultSubjectId) != null
    ) {
      fetchSubjectNotes();
    }
  }, [encryptionData, subjectId, defaultSubjectId, fetchSubjectNotes]);

  useEffect(() => setPubPvtKey(), [currentPage, subjectId, defaultSubjectId]);

  const filterNotes = (notes, searchText) => {
    if (!searchText) return notes;
    return notes.filter((note) =>
      note.title.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  return (
    <div>
      <ScrollArea className="w-full lg:w-[535px] rounded-md border px-4 overflow-auto max-h-[600px]">
        {filterNotes(notesData?.notes, searchText)?.map((ele, i) => (
          <div
            key={i}
            className="bg-[#F8FCFD] border border-[#DFE3EC] rounded-lg lg:p-4 p-2 my-3 flex items-center justify-between gap-2 w-full"
          >
            {/* left section */}
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <div className="bg-white p-2 rounded-full w-[3rem] opacity-80">
                <img src={PDF_ICON} alt="pdf icon" loading="lazy" />
              </div>
              <div className="md:w-[11rem] w-full font-semibold md:truncate break-words">
                {ele?.title}
              </div>
            </div>
            {/* Right Section */}
            <div className="md:mr-4 shrink-0 flex items-center">
              {!notesData?.is_locked && (
                <div>
                  <PDFDialogButton
                    BtnStyle="
    border border-[#5A277C]
    text-[#5A277C]
    px-2 py-[3px]
    rounded-full
    text-xs
    font-medium
    whitespace-nowrap
     flex items-center justify-center
     min-w-[20px]
    hover:bg-[#5A277C]
    hover:text-white
    transition
  "
                    BtnText="VIEW"
                    BtnTextStyle=""
                    ShowBtnText={true}
                    pdfUrl={DecodePdfUrl(
                      notesData,
                      ele?.note_pdf,
                      encryptionData
                    )}
                    DownloadFileName={ele?.title}
                    showIcon={false}
                    showDownloadButton={
                      userAuthenticated &&
                      String(isPurchased) === "1" &&
                      Number(data?.is_pdf_available_in_db) === 1
                    }
                  />

                </div>
              )}

              {notesData?.is_locked && (
                <div>
                  <Dialog>
                    <DialogTrigger>
                      <div className="bg-[#5A277C] p-2 rounded-full cursor-pointer">
                        <Lock className="text-white" />
                      </div>
                    </DialogTrigger>

                    <DialogContent
                      className="
      w-[90%]
      max-w-sm
      sm:max-w-md
      lg:max-w-lg
      h-auto
      p-5
      rounded-xl
    "
                    >
                      <DialogHeader className="text-center">
                        <DialogTitle className="text-xl sm:text-2xl text-[#5A277C] font-semibold">
                          Buy Course
                        </DialogTitle>

                        <DialogDescription className="mt-2 text-sm sm:text-base">
                          To access this course. Please purchase this course 😊
                        </DialogDescription>
                      </DialogHeader>

                      <DialogFooter className="mt-4 flex justify-center">
                        <Button
                          type="button"
                          className="bg-[#5A277C] hover:bg-purple-700 w-full sm:w-auto"
                          onClick={() =>
                            handleBuy(
                              dispatch,
                              navigate,
                              data?.id,
                              ITEM_COURSE,
                              data?.name,
                              data?.offer_price,
                              data?.offer_discount,
                              data?.price,
                              data?.principal_amount,
                              data?.course_banner,
                              data?.course_icon
                            )
                          }
                        >
                          Buy Now
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                </div>
              )}
            </div>
          </div>
        ))}
      </ScrollArea>
    </div>
  );
};

export default ClassroomTabNotes;
